import React, { forwardRef } from 'react'
import { twMerge } from 'tailwind-merge'

import { ChevronDownIcon } from '../../_gluestack/icon'
import {
    Select as GsSelect,
    SelectIcon as GsSelectIcon,
    SelectInput as GsSelectInput,
    SelectTrigger as GsSelectTrigger,
} from '../../_gluestack/select'

// Prop-driven AOS Select (dropdown) — owns the Figma trigger styling (node
// 7194:125121) and wraps the vendored Gluestack compound, which keeps the
// interaction wiring (data-[hover|focus|invalid|disabled] on the trigger) and
// the Actionsheet menu. The `_gluestack/select` tva is neutralized to
// structure-only, so this file is the single source of truth for the trigger.
// States mirror Input: default border/subtle on bg/input → hover border/strong +
// input-hover bg → focus 2px focus/ring → invalid 2px status/critical → disabled
// 40% opacity (group-level via FormControl, or here when standalone).

type GsSelectRef = React.ComponentRef<typeof GsSelect>
export type SelectProps = React.ComponentPropsWithoutRef<typeof GsSelect> & {
    className?: string
}

export const Select = forwardRef<GsSelectRef, SelectProps>(({ className, ...rest }, ref) => (
    <GsSelect
        ref={ref}
        className={twMerge(rest.isDisabled ? 'opacity-40' : '', className)}
        {...rest}
    />
))
Select.displayName = 'Select'

const triggerBase = twMerge(
    'flex-row items-center overflow-hidden rounded bg-aos-bg-input h-10 px-3 gap-3',
    'border border-border-subtle',
    'data-[hover=true]:border-border-strong data-[hover=true]:bg-aos-bg-input-hover',
    'data-[focus=true]:border-2 data-[focus=true]:border-action-focus-ring',
    'data-[hover=true]:data-[focus=true]:border-2 data-[hover=true]:data-[focus=true]:border-action-focus-ring',
    'data-[invalid=true]:border-2 data-[invalid=true]:border-status-critical',
    // Disabled: not hoverable / clickable; revert any hover styling. The 40% dim
    // comes from the group (FormControl) or the Select root prop above.
    'data-[disabled=true]:border-border-subtle data-[disabled=true]:pointer-events-none',
    'data-[disabled=true]:data-[hover=true]:border-border-subtle data-[disabled=true]:data-[hover=true]:bg-aos-bg-input',
)

type GsSelectTriggerRef = React.ComponentRef<typeof GsSelectTrigger>
export type SelectTriggerProps = Omit<
    React.ComponentPropsWithoutRef<typeof GsSelectTrigger>,
    'children'
> & {
    className?: string
    children?: React.ReactNode
}

// Figma always shows a trailing chevron — bake it in so every dropdown matches
// the design without the caller composing it.
export const SelectTrigger = forwardRef<GsSelectTriggerRef, SelectTriggerProps>(
    ({ className, children, ...rest }, ref) => (
        <GsSelectTrigger ref={ref} className={twMerge(triggerBase, className)} {...rest}>
            {children}
            <SelectIcon as={ChevronDownIcon} />
        </GsSelectTrigger>
    ),
)
SelectTrigger.displayName = 'SelectTrigger'

// Figma field text: content/default ink, content/muted placeholder, P2 14.22.
const selectInputBase =
    'flex-1 px-0 font-regular text-[0.875rem] leading-[1.25rem] text-aos-text-default placeholder:text-aos-text-muted'

type GsSelectInputRef = React.ComponentRef<typeof GsSelectInput>
export type SelectInputProps = React.ComponentPropsWithoutRef<typeof GsSelectInput> & {
    className?: string
}

export const SelectInput = forwardRef<GsSelectInputRef, SelectInputProps>(
    ({ className, ...rest }, ref) => (
        <GsSelectInput ref={ref} className={twMerge(selectInputBase, className)} {...rest} />
    ),
)
SelectInput.displayName = 'SelectInput'

type GsSelectIconRef = React.ComponentRef<typeof GsSelectIcon>
export type SelectIconProps = React.ComponentPropsWithoutRef<typeof GsSelectIcon> & {
    className?: string
}

export const SelectIcon = forwardRef<GsSelectIconRef, SelectIconProps>(
    ({ className, size = 'md', ...rest }, ref) => (
        <GsSelectIcon
            ref={ref}
            size={size}
            className={twMerge('h-6 w-6 text-aos-text-muted', className)}
            {...rest}
        />
    ),
)
SelectIcon.displayName = 'SelectIcon'
